import { useToggle } from "hooks/useToggle";
import React, { useCallback, useState } from "react";
import { AdminUnitsForm } from "./AdminUnitsForm";
import { AddUnitEmployeeForm } from "./AddUnitEmployeeForm";
import { AdminUnitsBillingForm } from "./AdminUnitsBillingForm";
import { AssignEquipmentForm } from "./AssignEquipmentForm";
import { DispenserForm } from "./DispenserForm";
import { ActionsButtons } from "components/CustomButtons/ActionsButtons";
import {
  AccountBalance as AccountBalanceIcon,
  Edit,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  Router as RouterIcon,
} from "@mui/icons-material";
import BadgeIcon from "@mui/icons-material/Badge";
import { useDispatch, useSelector } from "react-redux";
import { getEmployees } from "redux/actions/employeesCatalog/employeesCatalogActions";
import DeleteIcon from "@mui/icons-material/Delete";
import { ModalV2 } from "components/Modal/ModalV2";
import {
  removeAdminUnitsAction,
  togglePaymentTerminalAction,
  updateUnitInventory
} from "redux/actions/adminUnits/adminUnitsActions";
import { PERMISSIONS } from "router/permissions";
import { useRuleValidator } from "hooks/useRuleValidator";
import DiscountIcon from "@mui/icons-material/Discount";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PersonIcon from "@mui/icons-material/Person";
import { LinkUserCustomer } from "../ConfigSync/components/LinkUserCustomer";
import { UpdateDiscountForm } from "./UpdateDiscountForm";
import { formatNumber } from "helpers/getNumberFormats";
import WholesalerCreditConditionsForm from "./WholesalerCreditConditionsForm";
import { getCreditLimitAction, fetchLinkedCustomers, getWholesalerConditions } from "../../redux/actions/adminUnits/adminUnitsActions";

const wholesalersUnitTypes = ["MAYORISTA_E", "MAYORISTA_P"];
const disallowedUnitTypes = ["ANDEN", "VENTA_PLANTA", "UTILITARIO"];

export const columns = [
  {
    Header: "Numero",
    accessor: "number",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row?.number)?.toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: "Tipo",
    accessor: "type",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row?.type)?.toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: "Capacidad",
    accessor: "capacity",
    filterable: true,
    filterMethod: (filter, row) =>
      row.capacity
        ?.toString()
        .toLowerCase()
        .includes(filter.value.toLowerCase()),
    Cell: ({ value }) => <span>{formatNumber(value)} Lt</span>,
  },
  {
    Header: "Empleado",
    accessor: "driverName",
    filterable: true,
    filterMethod: (filter, row) =>
      row?.driverName?.toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: "Permiso",
    accessor: "creLicense",
    filterable: true,
    filterMethod: (filter, row) =>
      row.creLicense?.toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: "Placas",
    accessor: "unitPlates",
    filterable: true,
    filterMethod: (filter, row) =>
      row?.unitPlates?.toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: "U. Operativa",
    accessor: "operatingStation",
    filterable: true,
    filterMethod: (filter, row) =>
      row?.operatingStation?.toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: "Acciones",
    Cell: ({ original }) => {
      const {
        UNIT_CATALOG_EDIT,
        UNIT_ADD_EMPLOYEE,
        UNIT_CATALOG_DEL,
        UNITS_INVENTORY_WHOLE,
        UNIT_CATALOG_PAYMENT_TERMINAL,
        UNITS_INVOICE_TRANSFER_EDIT,
        UNIT_CATALOG_DISCOUNT,
        UNITS_WHOLESALER_CUSTOMERS,
        UNITS_WHOLESALER_CONDITIONS,
      } = PERMISSIONS;
      const { id: unitId, customer, conditionsFetched } = original;

      const isEdit = useRuleValidator(UNIT_CATALOG_EDIT);
      const isAdd = useRuleValidator(UNIT_ADD_EMPLOYEE);
      const isDel = useRuleValidator(UNIT_CATALOG_DEL);
      const canSubmitUnit = useRuleValidator(UNITS_INVENTORY_WHOLE);
      const isPaymentTerminal = useRuleValidator(UNIT_CATALOG_PAYMENT_TERMINAL);
      const canEditTransferBill = useRuleValidator(UNITS_INVOICE_TRANSFER_EDIT);
      const isDiscount = useRuleValidator(UNIT_CATALOG_DISCOUNT);
      const isWholesalerCustomers = useRuleValidator(UNITS_WHOLESALER_CUSTOMERS);
      const canSetConditions = useRuleValidator(UNITS_WHOLESALER_CONDITIONS);
      const canEditDispensers = useRuleValidator(PERMISSIONS.CATALOGS_UNIT_DISPENSERS_EDIT);
      const canEditEquipment = useRuleValidator(PERMISSIONS.CATALOGS_UNIT_EQUIPMENTS_EDIT);
      const isWholesaler = original.unit_type === "MAYORISTA_E" || original.unit_type === "MAYORISTA_P";
      const isEstacion = original.unit_type === "ESTACION";
      const isEstacionario = original.unit_type === "ESTACIONARIO";
      
      const [open, setOpen] = useToggle();
      const [openEmployee, setOpenEmployee] = useToggle();
      const [openDel, setOpenDel] = useToggle();
      const [openAddToInventory, setOpenAddToInventory] = useToggle();
      const [openBill, setOpenBill] = useToggle();
      const [openPercentage, setOpenPercentage] = useToggle();
      const [openTerminal, setOpenTerminal] = useToggle();
      const [linkAccountModal, handleLinkAccountModal] = useToggle();
      const [openCreditConditions, setOpenCreditConditions] = useToggle();
      const [openEquipment, setOpenEquipment] = useToggle();
      const [loadingConditions, setLoadingConditions] = useState(false);

      const dispatch = useDispatch();

      const { isLoadingComp } = useSelector(({ search }) => search);

      const { employeesArr } = useSelector(
        ({ employeesCatalog }) => employeesCatalog,
      );
      const { station } = useSelector(({ adminUnits }) => adminUnits);

      const hasInventory = original.conf?.hasInventory ?? false;
      const isWholesalerPorE = wholesalersUnitTypes.includes(original.unit_type || original.type);
      const invToUnitAction = hasInventory ? "Remover" : "Agregar";
      const invToUnitPreposition = hasInventory ? "del" : "al";
      const isUnitTypeAllowed = original.unit_type && !disallowedUnitTypes.includes(original.unit_type);

      const handleCreditConditionsModal = useCallback(async () => {
        setLoadingConditions(true);
        try {
          const customerId = customer?.idCustomer || await dispatch(fetchLinkedCustomers(unitId));
          if (!customerId) return;
          if (customer?.creditLimit == null) {
            dispatch(getCreditLimitAction({ customerId, unitId }));
          }
          if (!conditionsFetched) {
            await dispatch(getWholesalerConditions({ unitId }));
          }
          setOpenCreditConditions();
        } finally {
          setLoadingConditions(false);
        }
      }, [customer?.idCustomer, unitId, customer?.creditLimit, conditionsFetched]);
      
      const handleOpenEmployees = () => {
        if (station?.id && employeesArr.length > 0) {
          if (employeesArr[0].idStation !== station.id) {
            dispatch(getEmployees(station?.id, setOpenEmployee));
          } else {
            setOpenEmployee();
          }
        } else if (station?.id && employeesArr.length === 0) {
          dispatch(getEmployees(station?.id, setOpenEmployee));
        }
      };

      const handleDelete = () => {
        dispatch(removeAdminUnitsAction(original.id, setOpenDel));
      };

      const handleAddToInventory = async () => {
        const onSuccess = await dispatch(updateUnitInventory(original.id, { hasInventory: !hasInventory }));
        if (onSuccess) setOpenAddToInventory();
      };
      
      const handleToggleTerminal = () => {
        const newValue = !original.route?.hasPaymentTerminal;
        dispatch(togglePaymentTerminalAction(original.id, newValue, setOpenTerminal));
      };

      const hasRoute = original.route != null;
      const terminalActive = original.route?.hasPaymentTerminal === 1 || original.route?.hasPaymentTerminal === true;

      const buttons = [
        {
          Icon: AccountBalanceIcon,
          color: 0,
          onClick: handleCreditConditionsModal,
          tooltip: "Condiciones de crédito",
          loading: loadingConditions,
          validation: canSetConditions && isWholesalerPorE,
        },
        {
          Icon: Edit,
          color: 0,
          onClick: () => setOpen(),
          tooltip: "editar",
          validation: isEdit,
        },
        {
          Icon: BadgeIcon,
          color: 1,
          onClick: () => handleOpenEmployees(),
          tooltip: "Empleado",
          loading: isLoadingComp?.loadingEmployees,
          validation: isAdd,
        },
        {
          Icon: DiscountIcon,
          color: 0,
          onClick: () => setOpenPercentage(),
          tooltip: "Actualizar descuento por litro",
          loading: isLoadingComp?.updatePercentage,
          validation: isDiscount,
        },
        {
          Icon: DeleteIcon,
          color: 3,
          onClick: () => setOpenDel(),
          tooltip: "eliminar",
          loading: isLoadingComp?.delete,
          validation: isDel,
        },
        {
          Icon: LocalShippingIcon,
          color: hasInventory ? 1 : 3,
          onClick: () => setOpenAddToInventory(),
          tooltip: `${invToUnitAction} unidad ${invToUnitPreposition} inventario`,
          validation: canSubmitUnit && isWholesalerPorE,
        },
        {
          Icon: CreditCardIcon,
          color: !hasRoute ? 0 : terminalActive ? 4 : 0,
          onClick: () => setOpenTerminal(),
          tooltip: !hasRoute
            ? "La unidad no se encuentra en ruta"
            : terminalActive
              ? "Desactivar Terminal Bancaria"
              : "Activar Terminal Bancaria",
          loading: isLoadingComp?.toggleTerminal,
          validation: isPaymentTerminal,
          disabled: !hasRoute,
          intensity: !hasRoute ? 200 : 500,
        },
        {
          Icon: ReceiptIcon,
          color: 0,
          onClick: () => setOpenBill(),
          tooltip: "Factura de traslado",
          validation: canEditTransferBill && isUnitTypeAllowed,
        },
        {
          Icon: PersonIcon,
          color: 0,
          onClick: () => handleLinkAccountModal(),
          tooltip: "Ligar cuenta",
          validation: isWholesalerCustomers && isWholesaler,
        },
        {
          Icon: RouterIcon,
          color: 0,
          onClick: () => setOpenEquipment(),
          tooltip: isEstacion ? "Dispensadores" : "Equipo electrónico",
          validation: (canEditEquipment && isEstacionario) || (canEditDispensers && isEstacion),
        },
      ];

      return (
        <>
          <ActionsButtons buttons={buttons} />
          {openCreditConditions && (
            <WholesalerCreditConditionsForm
              title={`Condiciones de crédito para mayoristas - Unidad ${original.number}`}
              open={openCreditConditions}
              setOpen={setOpenCreditConditions}
              data={original}
            />
          )}
          {open && (
            <AdminUnitsForm
              title={`Editar unidad ${original.number}`}
              open={open}
              setOpen={setOpen}
              data={original}
              edit
            />
          )}
          {openEmployee && (
            <AddUnitEmployeeForm
              title={`Agregar empleado a unidad ${original.number}`}
              open={openEmployee}
              setOpen={setOpenEmployee}
              data={original}
            />
          )}
          <ModalV2
            title="¿Seguro que desea realizar esta acción?"
            open={openDel}
            onClose={setOpenDel}
            onSubmit={handleDelete}
            justConfirm
            isLoading={isLoadingComp?.delete}
            confirmColor="warning"
          />
          {openAddToInventory && (
            <ModalV2
              title={`¿Seguro que desea ${invToUnitAction.toUpperCase()} la unidad ${original.number} ${invToUnitPreposition} inventario?`}
              open={openAddToInventory}
              onClose={setOpenAddToInventory}
              onSubmit={handleAddToInventory}
              justConfirm
              isLoading={isLoadingComp?.unitToInventory}
              confirmColor="warning"
            />
          )}
          {openBill && (
            <AdminUnitsBillingForm
              title={`Configuración factura de traslados — Unidad ${original.number}`}
              open={openBill}
              setOpen={setOpenBill}
              data={original}
            />
          )}
          {openPercentage && (
            <UpdateDiscountForm
              open={openPercentage}
              setOpen={setOpenPercentage}
              unit={original}
            />
          )}
          {openTerminal && (
            <ModalV2
              title={`¿Desea ${terminalActive ? "desactivar" : "activar"} la terminal bancaria para la unidad ${original.number}?`}
              open={openTerminal}
              onClose={setOpenTerminal}
              onSubmit={handleToggleTerminal}
              justConfirm
              isLoading={isLoadingComp?.toggleTerminal}
            />
          )}
          {linkAccountModal && (
            <ModalV2
              open={linkAccountModal}
              onClose={handleLinkAccountModal}
              title="Ligar cuenta"
              toRight
            >
              <LinkUserCustomer data={original} onClose={handleLinkAccountModal} />
            </ModalV2>
          )}
          {openEquipment && isEstacionario && (
            <AssignEquipmentForm
              open={openEquipment}
              setOpen={setOpenEquipment}
              data={original}
              station={station}
            />
          )}
          {openEquipment && isEstacion && (
            <DispenserForm
              open={openEquipment}
              setOpen={setOpenEquipment}
              data={original}
              station={station}
            />
          )}
        </>
      );
    },
  },
];
